import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  TemplateRef,
  input,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EnumOptions } from '@kinet-open-network/shared/data-access/model';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { EnumColumnComponent, IndicatorComponent, ToastSeverity } from '../..';

export interface IBlockItem {
  header?: string;
  value?: any;
  template?: TemplateRef<unknown>;
  options?: {
    enum?: Map<string, EnumOptions>;
    hideIndicator?: boolean;
    date?: boolean;
    html?: boolean;
    onHtmlClick?: (event: MouseEvent) => void;
    button?: {
      icon?: string;
      command: () => void;
      label?: string;
      styleClass?: string;
      disabled?: boolean;
    };
  };
  button?: {
    icon: string;
    command: () => void;
    tooltip?: string;
    tooltipPosition?: string;
    visible?: boolean;
  };
}

@Component({
  selector: 'kon-blocks',
  standalone: true,
  imports: [
    CommonModule,
    IndicatorComponent,
    ButtonModule,
    TooltipModule,
    EnumColumnComponent,
  ],
  template: `
    <div
      class="surface-ground md:py-4 max-md:px-4 border-black rounded-lg shadow relative"
    >
      <h3 *ngIf="header()" class="font-normal mb-2 px-6">
        {{ header() }}
      </h3>
      <div
        class="grid max-sm:grid-flow-row md:grid-flow-col auto-cols-fr md:divide-x max-md:divide-y"
      >
        <div
          class="surface-card py-2 w-full md:px-6 relative"
          *ngFor="let item of data"
        >
          <div class="w-full overflow-hidden py-2 px-1">
            <span
              class="text-gray-500 font-sm w-full min-h-[1.5rem] flex items-center"
              >{{ item.header }}</span
            >
            <div class="text-900 font-medium">
              <ng-container *ngIf="item.template">
                <ng-container [ngTemplateOutlet]="item.template" />
              </ng-container>
              <ng-container *ngIf="!item.template">
                @if(item.options?.enum) { @if(!item.options?.hideIndicator) {
                <kon-indicator
                  [value]="item.value"
                  [options]="item.options?.enum"
                />
                } @else {
                <kon-enum-column
                  [value]="item.value"
                  [options]="item.options?.enum"
                />
                } }
                <p
                  *ngIf="
                    !item.value && !item.options?.enum && !item.options?.button
                  "
                >
                  --
                </p>
                <p *ngIf="item.options?.date">
                  {{ item.value | date }}
                </p>
                <p-button
                  *ngIf="item.options?.button"
                  [icon]="item.options?.button?.icon"
                  [label]="item.options?.button?.label"
                  [styleClass]="item.options?.button?.styleClass"
                  (onClick)="item.options?.button?.command()"
                  [disabled]="item.options?.button?.disabled"
                />
                <p
                  *ngIf="item.options?.html"
                  [innerHTML]="sanitizeHtml(item.value)"
                  (click)="item.options?.onHtmlClick?.($event)"
                ></p>
                <p
                  *ngIf="!item.options"
                  class="break-words whitespace-pre-line"
                >
                  {{ item.value }}
                </p>
              </ng-container>
            </div>
            @if (item.button && (item.button.visible === undefined ||
            item.button.visible)) {
            <p-button
              *ngIf="item.button"
              [icon]="item.button.icon"
              [rounded]="true"
              [text]="true"
              class="absolute top-0 right-2"
              size="small"
              (onClick)="item.button.command()"
              [pTooltip]="item.button.tooltip"
              [tooltipPosition]="item.button.tooltipPosition || 'right'"
            />
            }
          </div>
        </div>
      </div>
      <p-button
        *ngIf="action()"
        [icon]="action()?.icon"
        [rounded]="true"
        [text]="true"
        [severity]="action()?.severity"
        class="absolute top-2 right-2"
        (onClick)="action()?.command()"
        [disabled]="action()?.disabled"
      ></p-button>
    </div>
  `,
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlocksComponent {
  @HostBinding('class') class = 'block';
  @Input() data: IBlockItem[] = [];

  header = input<string>();
  action = input<{
    icon?: string;
    severity?: ToastSeverity;
    command: () => void;
    disabled?: boolean;
  }>();

  constructor(private domSanitizer: DomSanitizer) {}

  sanitizeHtml(value: any): SafeHtml {
    return this.domSanitizer.sanitize(1, value) || '';
  }
}
